import React, { useState, useEffect } from "react";
import "line-awesome/dist/line-awesome/css/line-awesome.min.css";
import { useRecoilValue } from "recoil";
import { cartState } from "../recoil/atoms/cartAtom";
import { motion, AnimatePresence } from "framer-motion";

const Footer = ({
  onCartClick,
  onHomeClick,
  onSearchClick,
  onAccountClick,
  activeTab: externalActiveTab,
  isSliderOpen,
}) => {
  const [activeTab, setActiveTab] = useState("home");
  const cartItems = useRecoilValue(cartState);
  const cartCount = cartItems.reduce((sum, item) => sum + (item.qty || 0), 0);
  const [cartIconControl, setCartIconControl] = useState(false);

  useEffect(() => {
    if (externalActiveTab) setActiveTab(externalActiveTab);
  }, [externalActiveTab]);

  useEffect(() => {
    const triggerBounce = () => {
      setCartIconControl(true);
      setTimeout(() => setCartIconControl(false), 600);
    };
    window.addEventListener("cart-bounce", triggerBounce);
    return () => window.removeEventListener("cart-bounce", triggerBounce);
  }, []);

  const tabs = [
    { id: "home", icon: "la-home", label: "Home" },
    { id: "search", icon: "la-search", label: "Search" },
    { id: "cart", icon: "la-shopping-cart", label: "Cart" },
    { id: "account", icon: "la-user", label: "Account" },
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "cart" && onCartClick) onCartClick();
    else if (tabId === "home" && onHomeClick) onHomeClick();
    else if (tabId === "search" && onSearchClick) onSearchClick();
    else if (tabId === "account" && onAccountClick) onAccountClick();
  };

  return (
    <footer
      className="fixed left-0 right-0 mx-auto w-[92%] max-w-[420px] rounded-2xl z-[9999] pointer-events-auto"
      style={{
        bottom: "16px",
        background: "rgba(255,255,255,0.9)",
        backdropFilter: "blur(28px) saturate(1.6)",
        WebkitBackdropFilter: "blur(28px) saturate(1.6)",
        border: "1px solid rgba(255,255,255,0.75)",
        boxShadow:
          "0 8px 32px rgba(0,0,0,0.1), 0 0 0 1px rgba(34,197,94,0.12), 0 1px 0 rgba(255,255,255,0.9) inset",
        opacity: isSliderOpen ? 0 : 1,
        pointerEvents: isSliderOpen ? "none" : "auto",
        transition: "opacity 0.3s ease",
        transform: "none",
        willChange: "auto",
      }}
    >
      <div className="px-1 flex justify-between items-center h-16">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              id={tab.id === "cart" ? "cart-nav-tab" : undefined}
              onClick={() => handleTabClick(tab.id)}
              className="relative flex flex-col items-center justify-center cursor-pointer px-2 py-2 rounded-lg z-10 select-none flex-1 min-w-0"
            >
              {/* Sliding active pill background */}
              {isActive && (
                <motion.div
                  layoutId="activeTabPill"
                  className="absolute inset-y-1.5 inset-x-2 rounded-lg"
                  style={{
                    background: "linear-gradient(135deg, rgba(0,113,59,0.15), rgba(34,197,94,0.09))",
                    border: "1px solid rgba(0,113,59,0.28)",
                  }}
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}

              {/* Icon with cart bounce + active glow */}
              <motion.div
                animate={
                  tab.id === "cart" && cartIconControl
                    ? { scale: [1, 1.4, 0.88, 1.12, 1], rotate: [0, -8, 8, 0] }
                    : isActive
                    ? { scale: 1 }
                    : { scale: 1 }
                }
                transition={{ duration: 0.55, ease: "easeInOut" }}
                className={`flex items-center justify-center transition-colors duration-300 relative ${
                  isActive ? "text-[#00713b] tab-active-glow" : "text-gray-500 hover:text-green-500"
                }`}
              >
                <i className={`la ${tab.icon} text-2xl sm:text-2xl md:text-3xl`} />

              </motion.div>

              {/* Cart Badge */}
              {tab.id === "cart" && cartCount > 0 && (
                <AnimatePresence>
                  <motion.span
                    key={cartCount}
                    initial={{ scale: 0.4, opacity: 0, y: 4 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.4, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 520, damping: 16 }}
                    className="absolute top-1.5 right-3.5 bg-red-500 text-white text-[10px] font-bold rounded-full
                    w-5 h-5 flex items-center justify-center"
                    style={{
                      boxShadow: "0 0 0 2px rgba(255,255,255,0.9), 0 2px 8px rgba(239,68,68,0.4)",
                    }}
                  >
                    {cartCount}
                  </motion.span>
                </AnimatePresence>
              )}

              {/* Tab Label */}
              <motion.span
                animate={{ color: isActive ? "#00713b" : "#6b7280" }}
                transition={{ duration: 0.25 }}
                className="text-xs sm:text-sm font-semibold mt-0.5"
              >
                {tab.label}
              </motion.span>
            </div>
          );
        })}
      </div>
    </footer>
  );
};

export default Footer;
